import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChatDisclaimer } from "@/components/AiNotice";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, Send, Loader2, Trash2, Leaf, Sparkles, Globe, TreePine } from "lucide-react";
import { chatGaia, chatGaiaHistory, chatGaiaClear, chatGaiaUsage } from "@/lib/api";
import { useLang } from "@/lib/lang";
import { useAccount } from "@/lib/account";
import { PLAYLISTS, ACCENTS } from "@/lib/tracks";
import MusicPicker from "@/components/MusicPicker";
import VoiceButton from "@/components/VoiceButton";

const SESSION_KEY = "astrovoyance.gaia_session";

const SUGGESTIONS_FR = [
  "Comment me reconnecter à la nature au quotidien ?",
  "Explique-moi la géobiologie simplement.",
  "Un geste bon pour la Terre à faire aujourd'hui ?",
  "Je me sens coupée du vivant, aide-moi.",
  "Qu'est-ce que vivre dans le présent ?",
];
const SUGGESTIONS_EN = [
  "How can I reconnect with nature daily?",
  "Explain geobiology simply.",
  "One Earth-friendly gesture to do today?",
  "I feel cut off from the living, help me.",
  "What is living in the present?",
];

export default function Gaia() {
  const { lang, setLang } = useLang();
  const { email, status } = useAccount() || {};
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || "");
  const [usage, setUsage] = useState({ used_today: 0, limit: null, premium_active: true, remaining: null });
  const boxRef = useRef(null);

  useEffect(() => {
    if (!sessionId) return;
    chatGaiaHistory(sessionId).then((d) => setMessages(d.messages || [])).catch(() => {});
  }, [sessionId]);

  useEffect(() => {
    chatGaiaUsage(email).then(setUsage).catch(() => {});
  }, [email, status?.premium_active, messages.length]);

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed) return;
    setMessages((m) => [...m, { sender: "user", message: trimmed, created_at: new Date().toISOString() }]);
    setInput("");
    setLoading(true);
    try {
      const r = await chatGaia(trimmed, sessionId, email, lang);
      if (r.session_id && r.session_id !== sessionId) {
        setSessionId(r.session_id);
        localStorage.setItem(SESSION_KEY, r.session_id);
      }
      setMessages((m) => [...m, { sender: "advisor", message: r.reply, created_at: new Date().toISOString() }]);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429 && detail?.code === "daily_gaia_limit_reached") {
        toast.error(lang === "fr"
          ? `Gaïa t'a enveloppé·e ${detail.limit} fois. Repose-toi, respire. Reviens demain ou passe Premium.`
          : `Gaia enveloped you ${detail.limit} times. Rest, breathe. Come back tomorrow or go Premium.`);
        setUsage((u) => ({ ...u, used_today: detail.used_today, remaining: 0 }));
      } else {
        toast.error(lang === "fr" ? "Gaïa écoute le vent. Réessaie dans un instant." : "Gaia listens to the wind. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const wipe = async () => {
    try { await chatGaiaClear(sessionId); } catch { /* ignore */ }
    localStorage.removeItem(SESSION_KEY);
    setSessionId("");
    setMessages([]);
    toast.success(lang === "fr" ? "Session effacée. Marche dehors." : "Session wiped. Walk outside.");
  };

  const suggestions = useMemo(() => lang === "fr" ? SUGGESTIONS_FR : SUGGESTIONS_EN, [lang]);

  return (
    <div data-testid="gaia-page" className="gaia-root min-h-screen relative">
      <div className="gaia-bg" aria-hidden="true" />
      <header className="relative z-30">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-6 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-[0.24em]" style={{ color: "#7bc47f" }}>
            <ArrowLeft size={14} /> {lang === "fr" ? "Retour" : "Back"}
          </Link>
          <div className="flex items-center gap-2">
            <div className="flex items-center rounded-full text-[11px] font-mono overflow-hidden"
                 style={{ background: "rgba(40,80,55,0.4)", border: "1px solid rgba(145,200,160,0.3)" }}>
              <button onClick={() => setLang("fr")} type="button" className="px-2.5 py-1.5"
                      style={{ background: lang === "fr" ? "#7bc47f" : "transparent", color: lang === "fr" ? "#0a1a0e" : "#cfe6d3", fontWeight: lang === "fr" ? 600 : 400 }}>FR</button>
              <button onClick={() => setLang("en")} type="button" className="px-2.5 py-1.5"
                      style={{ background: lang === "en" ? "#7bc47f" : "transparent", color: lang === "en" ? "#0a1a0e" : "#cfe6d3", fontWeight: lang === "en" ? 600 : 400 }}>EN</button>
            </div>
            <MusicPicker playlist={PLAYLISTS.gaia} accent={ACCENTS.gaia} testId="gaia-music" persona="Gaïa" />
          </div>
        </div>
      </header>

      <section className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 pt-4 pb-8">
        <div className="overline mb-4" style={{ color: "#7bc47f" }}>
          <span className="kicker-line" />
          {lang === "fr" ? "Gaïa · déesse-mère de la Terre · gratuit illimité" : "Gaia · Mother-Earth goddess · free unlimited"}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02]" style={{ color: "#f0f6e8" }}>
          {lang === "fr" ? (
            <><span className="italic" style={{ color: "#7bc47f" }}>Gaïa</span>, Mère de la Terre.</>
          ) : (
            <><span className="italic" style={{ color: "#7bc47f" }}>Gaia</span>, Mother of the Earth.</>
          )}
        </h1>
        <p className="mt-5 max-w-3xl text-base sm:text-lg" style={{ color: "#cfe6d3" }}>
          {lang === "fr"
            ? "La planète est notre maison. Nous sommes citoyen·nes cosmiques, planétaires, nourricier·ères. Gaïa te réconforte, t'ancre dans l'instant présent, et t'enseigne la nature, l'écologie, la géobiologie et le bien-être incarné."
            : "The planet is our home. We are cosmic, planetary, nurturing citizens. Gaia comforts you, anchors you in the present moment, and teaches you nature, ecology, geobiology, and embodied well-being."}
        </p>

        <div data-testid="gaia-usage"
             className="mt-6 inline-flex items-center gap-3 text-xs font-mono px-4 py-2 rounded-full"
             style={{ background: "rgba(14,32,20,0.65)",
                      border: "1px solid rgba(123,196,127,0.4)",
                      color: "#7bc47f" }}>
          <Leaf size={13} />
          {lang === "fr" ? "Illimité · Toujours gratuit" : "Unlimited · Always free"}
        </div>
      </section>

      <section className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 pb-24">
        <div className="gaia-chat" data-testid="gaia-chat-box">
          <div ref={boxRef} className="gaia-messages">
            {messages.length === 0 && (
              <div className="gaia-empty">
                <TreePine size={22} style={{ color: "#7bc47f" }} />
                <p className="mt-3 font-serif text-xl italic" style={{ color: "#f0f6e8" }}>
                  {lang === "fr"
                    ? "« Respire. Pose la main sur ton ventre. Je t'écoute, mon enfant. »"
                    : "\"Breathe. Place your hand on your belly. I am listening, my child.\""}
                </p>
                <p className="mt-4 text-xs font-mono uppercase tracking-[0.2em]" style={{ color: "#a8c9b1" }}>
                  {lang === "fr" ? "Ou choisis une pierre à poser :" : "Or pick a stone to place:"}
                </p>
                <div className="mt-4 flex flex-wrap gap-2 justify-center">
                  {suggestions.map((s, i) => (
                    <button key={i} type="button"
                            data-testid={`gaia-suggestion-${i}`}
                            onClick={() => send(s)}
                            className="gaia-chip">
                      <Globe size={11} /> {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`gaia-msg ${m.sender === "user" ? "user" : "advisor"}`}>
                <div className="gaia-msg-inner">
                  {m.sender === "advisor" ? <ReactMarkdown>{m.message}</ReactMarkdown> : <span>{m.message}</span>}
                  {m.sender === "advisor" && (
                    <div className="mt-2">
                      <VoiceButton text={m.message} lang={lang} accent="#7bc47f" testId={`gaia-voice-${i}`} />
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="gaia-msg advisor">
                <div className="gaia-msg-inner inline-flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" />
                  <span className="text-sm" style={{ color: "#cfe6d3" }}>{lang === "fr" ? "Gaïa respire profondément…" : "Gaia breathes deeply…"}</span>
                </div>
              </div>
            )}
          </div>

          <form className="gaia-form" onSubmit={(e) => { e.preventDefault(); send(); }}>
            <textarea data-testid="gaia-input"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                      placeholder={lang === "fr" ? "Parle à la Terre-Mère…" : "Speak to the Mother Earth…"}
                      rows={2}
                      className="gaia-textarea"
                      disabled={loading} />
            <div className="flex items-center gap-2">
              <button type="button" onClick={wipe} data-testid="gaia-clear" className="gaia-btn-ghost">
                <Trash2 size={14} />
              </button>
              <button type="submit" disabled={loading || !input.trim()} data-testid="gaia-send" className="gaia-btn-primary">
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {lang === "fr" ? "Envoyer" : "Send"}
              </button>
            </div>
          </form>
          <ChatDisclaimer testId="gaia-ai-disclaimer" />
        </div>
      </section>

      <style>{`
        .gaia-root { background: radial-gradient(ellipse at top, #0f2416 0%, #061409 55%, #030a05 100%); color: #f0f6e8; overflow-x: hidden; }
        .gaia-bg { position: fixed; inset: 0; z-index: 0; pointer-events: none;
          background:
            radial-gradient(circle at 22% 28%, rgba(123,196,127,0.18) 0%, transparent 45%),
            radial-gradient(circle at 78% 72%, rgba(85,155,100,0.14) 0%, transparent 44%),
            linear-gradient(180deg, #0f2416 0%, #050e07 100%); }
        .gaia-chat { background: linear-gradient(180deg, rgba(14,32,22,0.9), rgba(5,14,8,0.94)); border: 1px solid rgba(123,196,127,0.32); border-radius: 22px; padding: 20px; box-shadow: 0 24px 60px -30px rgba(0,0,0,0.7); }
        .gaia-messages { min-height: 380px; max-height: 62vh; overflow-y: auto; padding: 12px 4px 20px; margin-bottom: 16px; }
        .gaia-empty { text-align: center; padding: 32px 20px; }
        .gaia-chip { display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; border-radius: 999px; background: rgba(50,100,65,0.4); border: 1px solid rgba(123,196,127,0.35); color: #d7e8d8; font-size: 12px; font-family: "IBM Plex Mono", monospace; transition: all 200ms ease; }
        .gaia-chip:hover { background: rgba(123,196,127,0.22); border-color: rgba(123,196,127,0.75); color: #f0f6e8; }
        .gaia-msg { display: flex; margin: 10px 0; }
        .gaia-msg.user { justify-content: flex-end; }
        .gaia-msg.advisor { justify-content: flex-start; }
        .gaia-msg-inner { max-width: 82%; padding: 14px 18px; border-radius: 16px; line-height: 1.6; font-size: 14.5px; }
        .gaia-msg.user .gaia-msg-inner { background: linear-gradient(135deg, #6e9a72, #4a7050); color: #f8f4e8; border-bottom-right-radius: 4px; }
        .gaia-msg.advisor .gaia-msg-inner { background: rgba(18,36,24,0.9); color: #f0f6e8; border: 1px solid rgba(123,196,127,0.24); border-bottom-left-radius: 4px; }
        .gaia-msg-inner p { margin: 0 0 10px; }
        .gaia-msg-inner p:last-child { margin-bottom: 0; }
        .gaia-msg-inner strong { color: #7bc47f; }
        .gaia-msg-inner em { color: #cfe6d3; }
        .gaia-form { display: flex; gap: 10px; align-items: flex-end; border-top: 1px solid rgba(123,196,127,0.2); padding-top: 14px; }
        .gaia-textarea { flex: 1; resize: none; padding: 12px 14px; border-radius: 14px; background: rgba(10,22,14,0.75); border: 1px solid rgba(123,196,127,0.28); color: #f0f6e8; font-family: inherit; font-size: 14.5px; outline: none; }
        .gaia-textarea:focus { border-color: rgba(123,196,127,0.75); }
        .gaia-btn-primary { display: inline-flex; align-items: center; gap: 8px; padding: 12px 18px; border-radius: 12px; background: linear-gradient(135deg, #7bc47f, #4a8550); color: #0a1a0e; font-weight: 600; font-size: 13px; font-family: "IBM Plex Mono", monospace; transition: transform 200ms ease, opacity 200ms ease; }
        .gaia-btn-primary:hover:not(:disabled) { transform: translateY(-1px); }
        .gaia-btn-primary:disabled { opacity: 0.4; cursor: not-allowed; }
        .gaia-btn-ghost { display: inline-flex; align-items: center; padding: 12px 14px; border-radius: 12px; background: rgba(180,40,40,0.15); border: 1px solid rgba(255,120,120,0.3); color: #ffb8a8; }
      `}</style>
    </div>
  );
}
